import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import MinimalNavigation from '../../components/portal/MinimalNavigation';

/**
 * BRAND COMPLETE SERVICE EXPERIENCE
 * Gold-standard reference for all deeper service pages
 * 8 Scenes: Hero → What It Means → Capabilities → Process → Work → Deliverables → Related Systems → Action
 */

const brandScenes = [
  {
    id: 'hero',
    type: 'hero'
  },
  {
    id: 'meaning',
    type: 'content',
    title: 'THE FOUNDATION',
    content: 'Brand is not decoration. It is the foundational system that defines how a business is recognized, remembered, and valued. Without it, growth is temporary. With it, everything else accelerates.'
  },
  {
    id: 'capabilities',
    type: 'capabilities',
    title: 'WHAT WE BUILD',
    items: [
      'Brand Strategy',
      'Positioning',
      'Naming & Messaging',
      'Logo Systems',
      'Visual Identity',
      'Typography',
      'Color Systems',
      'Brand Guidelines'
    ]
  },
  {
    id: 'process',
    type: 'process',
    title: 'HOW WE WORK',
    steps: ['DISCOVER', 'DEFINE', 'BUILD', 'APPLY', 'EVOLVE']
  },
  {
    id: 'work',
    type: 'work',
    title: 'SELECTED WORK'
  },
  {
    id: 'deliverables',
    type: 'deliverables',
    title: 'WHAT YOU RECEIVE',
    items: [
      'Complete Brand Strategy Document',
      'Logo System & Variations',
      'Typography Specification',
      'Color Palette & Usage',
      'Visual Language System',
      'Brand Guidelines',
      'Application Templates',
      'Launch Assets'
    ]
  },
  {
    id: 'connected',
    type: 'connected',
    title: 'CONNECTED SYSTEMS',
    systems: [
      { name: 'BRAND', active: true },
      { name: 'CONTENT', href: '/services/content' },
      { name: 'SOCIAL', href: '/services/social' },
      { name: 'GROWTH', href: '/services/growth' }
    ]
  },
  {
    id: 'action',
    type: 'action'
  }
];

export default function BrandExperience() {
  const [currentScene, setCurrentScene] = useState(0);
  const [direction, setDirection] = useState(0);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const isScrolling = useRef(false);

  const totalScenes = brandScenes.length;

  // Navigate to specific scene
  const navigateToScene = useCallback((newIndex) => {
    if (newIndex < 0) {
      // Return to main feed
      navigate('/');
      return;
    }
    if (newIndex >= totalScenes) return;
    
    setDirection(newIndex > currentScene ? 1 : -1);
    setCurrentScene(newIndex);
  }, [currentScene, totalScenes, navigate]);

  // Mouse wheel navigation
  const handleWheel = useCallback((e) => {
    if (isScrolling.current) return;
    
    isScrolling.current = true;
    
    if (e.deltaY > 0) {
      navigateToScene(currentScene + 1);
    } else if (e.deltaY < 0) {
      navigateToScene(currentScene - 1);
    }
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 800);
  }, [currentScene, navigateToScene]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigateToScene(currentScene + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateToScene(currentScene - 1);
      } else if (e.key === 'Escape') {
        navigate('/');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentScene, navigateToScene, navigate]);

  // Touch swipe navigation
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    const swipeDistance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (swipeDistance > minSwipeDistance) {
      navigateToScene(currentScene + 1);
    } else if (swipeDistance < -minSwipeDistance) {
      navigateToScene(currentScene - 1);
    }
  };

  const scene = brandScenes[currentScene];

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-[#0A0A0F] overflow-hidden"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Logo Top-Left */}
      <div className="fixed top-6 left-6 z-50">
        <button onClick={() => navigate('/')}>
          <img
            src="/logos/eternity-wordmark.png"
            alt="Eternity"
            className="h-8 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity duration-300"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </button>
      </div>

      {/* Minimal Navigation */}
      <MinimalNavigation />

      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-20 z-50 text-white/60 hover:text-white text-xs uppercase tracking-wider transition-colors duration-300"
      >
        ← BACK TO FEED
      </button>

      {/* Grain Texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.02] z-50 mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' /%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '180px'
        }}
      />

      {/* Scene Container */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentScene}
          custom={direction}
          initial={{ y: direction > 0 ? '100%' : '-100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: direction > 0 ? '-100%' : '100%', opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          {scene.type === 'hero' && <HeroScene />}
          {scene.type === 'content' && <ContentScene scene={scene} />}
          {scene.type === 'capabilities' && <CapabilitiesScene scene={scene} />}
          {scene.type === 'process' && <ProcessScene scene={scene} />}
          {scene.type === 'work' && <WorkScene scene={scene} />}
          {scene.type === 'deliverables' && <DeliverablesScene scene={scene} />}
          {scene.type === 'connected' && <ConnectedScene scene={scene} />}
          {scene.type === 'action' && <ActionScene navigate={navigate} />}
        </motion.div>
      </AnimatePresence>

      {/* Progress Indicator */}
      <div className="fixed right-5 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-2">
        {brandScenes.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentScene(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentScene
                ? 'w-1 h-8 bg-gradient-to-b from-[#FF6B35] to-[#FF8F66]'
                : 'w-1 h-1 bg-white/15 hover:bg-white/30'
            }`}
          />
        ))}
      </div>

      {/* Navigation Hint */}
      {currentScene === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="fixed bottom-7 left-1/2 -translate-x-1/2 z-40 text-[10px] font-medium uppercase tracking-[0.15em] text-[#636366]"
          style={{ fontFamily: "'SF Mono', monospace" }}
        >
          SCROLL TO CONTINUE
        </motion.div>
      )}
    </div>
  );
}

// Scene 1: Hero
function HeroScene() {
  return (
    <div className="absolute inset-0">
      {/* Background - Mountain/Foundation Metaphor */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
      >
        <img
          src="https://images.pexels.com/photos/12694101/pexels-photo-12694101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
          alt="Brand Foundation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0F]/40 via-[#0A0A0F]/70 to-[#0A0A0F]/95" />
      </motion.div>

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 lg:p-24">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-5xl"
        >
          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#636366] mb-4">
            IMMOVABLE FOUNDATION & VISION
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none uppercase text-white mb-6"
              style={{ letterSpacing: '-0.025em', lineHeight: '1.05' }}>
            BRAND
          </h1>
          
          <p className="text-lg sm:text-xl font-light text-white/90 leading-relaxed max-w-3xl">
            Build the foundation everything else grows from.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

// Scene 2: What It Means
function ContentScene({ scene }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 md:p-16">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl text-center"
      >
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#FF6B35] mb-8">
          {scene.title}
        </div>
        
        <p className="text-2xl sm:text-3xl lg:text-4xl font-light text-white/95 leading-relaxed">
          {scene.content}
        </p>
      </motion.div>
    </div>
  );
}

// Scene 3: Capabilities
function CapabilitiesScene({ scene }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 md:p-16">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-6xl w-full"
      >
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#636366] mb-12 text-center">
          {scene.title}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {scene.items.map((item, index) => (
            <motion.div
              key={item}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="relative group"
            >
              <div className="bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.06] hover:border-[#FF6B35]/30 rounded-2xl p-6 transition-all duration-300">
                <div className="text-sm font-semibold text-white/90 uppercase tracking-wide">
                  {item}
                </div>
                
                {/* Subtle ember accent on hover */}
                <div className="absolute top-0 left-0 w-1 h-0 group-hover:h-full bg-gradient-to-b from-[#FF6B35] to-[#FF8F66] rounded-l-2xl transition-all duration-300" />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// Scene 4: Process
function ProcessScene({ scene }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 md:p-16">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl w-full"
      >
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#636366] mb-16 text-center">
          {scene.title}
        </div>
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {scene.steps.map((step, index) => (
            <React.Fragment key={step}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative flex flex-col items-center"
              >
                {/* Step Number */}
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B35]/20 to-[#FF8F66]/10 border border-[#FF6B35]/30 flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-[#FF6B35]">
                    {index + 1}
                  </span>
                </div>
                
                {/* Step Name */}
                <div className="text-base font-semibold text-white uppercase tracking-wider">
                  {step}
                </div>
              </motion.div>
              
              {/* Arrow between steps */}
              {index < scene.steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 + 0.2 }}
                  className="hidden md:block text-[#FF6B35]/40"
                >
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// Scene 5: Selected Work
function WorkScene({ scene }) {
  // Placeholder for portfolio work - will be replaced with real work
  const placeholderProjects = [
    { id: 1, image: 'https://images.unsplash.com/photo-1634942537034-2531766767d1?w=600&h=800&fit=crop' },
    { id: 2, image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&h=800&fit=crop' },
    { id: 3, image: 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?w=600&h=800&fit=crop' }
  ];

  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 md:p-16">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl w-full"
      >
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#636366] mb-12 text-center">
          {scene.title}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {placeholderProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer"
            >
              <img
                src={project.image}
                alt="Brand Work"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F]/90 via-[#0A0A0F]/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
              
              {/* Placeholder indicator */}
              <div className="absolute bottom-4 left-4 text-xs uppercase tracking-wider text-white/60">
                Portfolio Work
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center mt-8 text-sm text-[#636366]">
          <span className="italic">Portfolio imagery: Placeholder for client work</span>
        </div>
      </motion.div>
    </div>
  );
}

// Scene 6: Deliverables
function DeliverablesScene({ scene }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 md:p-16">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-5xl w-full"
      >
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#636366] mb-12 text-center">
          {scene.title}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {scene.items.map((item, index) => (
            <motion.div
              key={item}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-[#FF6B35]/20 transition-colors duration-300"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]" />
              <div className="text-sm text-white/90">{item}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// Scene 7: Connected Systems
function ConnectedScene({ scene }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 md:p-16">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl w-full text-center"
      >
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#636366] mb-12">
          {scene.title}
        </div>
        
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {scene.systems.map((system, index) => (
            <React.Fragment key={system.name}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {system.active ? (
                  <div className="px-8 py-4 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8F66] text-white font-semibold text-lg uppercase tracking-wide">
                    {system.name}
                  </div>
                ) : (
                  <button
                    onClick={() => system.href && (window.location.href = system.href)}
                    className="px-8 py-4 rounded-full border border-white/10 hover:border-[#FF6B35]/40 text-white/70 hover:text-white font-semibold text-lg uppercase tracking-wide transition-all duration-300"
                  >
                    {system.name}
                  </button>
                )}
              </motion.div>
              
              {index < scene.systems.length - 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 + 0.2 }}
                  className="text-[#FF6B35]/40"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </div>
        
        <p className="mt-12 text-sm text-[#8E8E93] max-w-2xl mx-auto leading-relaxed">
          Eternity operates as a connected system. Each capability amplifies the others.
        </p>
      </motion.div>
    </div>
  );
}

// Scene 8: Final Action
function ActionScene({ navigate }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center"
      >
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 uppercase tracking-tight">
          BUILD WITH<br />ETERNITY
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate('/#contact')}
            className="group px-10 py-5 text-base font-semibold tracking-wide text-white bg-gradient-to-r from-[#FF6B35] to-[#FF8F66] rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,107,53,0.6)] hover:scale-105"
          >
            START A PROJECT
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="px-10 py-5 text-base font-semibold tracking-wide text-white border border-white/20 hover:border-white/40 rounded-full transition-all duration-300"
          >
            EXPLORE OUR WORK
          </button>
        </div>
      </motion.div>
    </div>
  );
}
